import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnInit,
  Signal,
  viewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '@environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {VehicleType} from '@service-and-repairs/awpintegrationlib';
import {AuthInfoFactory} from '../../auth/auth-info-factory';
import {AuthenticationTokenBearerService} from '../../auth/authentication-token-bearer.service';
import {CONFIG_CHANGE_RELOAD_MARKER} from '../../core/analytics/enums/process-kpi-opt-in-origin';
import {AppsService} from '../../core/apps/services/apps.service';
import {ConfigurationService} from '../../core/configuration/services/configuration.service';
import {ServiceCaseHolder} from '../../core/service-case/models/service-case-holder';
import {UserService} from '../../core/user/services/user.service';
import {NotificationsService} from '../../services/notifications.service';
import {Util} from '../../util/util';

@Component({
  selector: 'app-configuration-overlay',
  templateUrl: './configuration-overlay.component.html',
  styleUrls: ['./configuration-overlay.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ConfigurationOverlayComponent implements OnInit {
  private readonly userService: UserService = inject(UserService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly appsService: AppsService = inject(AppsService);
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService = inject(
    AuthenticationTokenBearerService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);

  readonly viewState: InputSignal<any> = input<any>();

  businessPartnerId: string;
  businessNumber: string;
  distributionPartnerNumber: string;
  outletNumber: string;

  readonly uegAwpConfigurationWebComponent: Signal<ElementRef> = viewChild(
    'uegAwpConfigurationWebComponent',
    {read: ElementRef}
  );

  locale: string;
  hub: string;
  etkConfigurationBaseUrl: string;
  printConfigurationBaseUrl: string;
  vehicleType: VehicleType;
  brands: string;
  authHeaders: string;
  authInfo: string;

  constructor() {
    this.hub = environment.hub;

    this.userService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => this.setUserData());

    this.onAuthTokenChanged();
    this.authenticationTokenBearerService.oidcTokenSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onAuthTokenChanged.bind(this));
    this.authenticationTokenBearerService.csslTokenSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onAuthTokenChanged.bind(this));
  }


  ngOnInit(): void {
    Util.attachScript('/ui/configuration/main.mjs')
      .then(() => this.uegAwpConfigurationWebComponent().nativeElement.api.show())
      .catch(() => this.notificationsService.showErrorNotification(this.translate.instant('config.loadError')));
  }

  onConfigChange(): void {
    this.notificationsService.showSuccessNotification(this.translate.instant('config.saveSuccess'));
    sessionStorage.setItem(CONFIG_CHANGE_RELOAD_MARKER, 'true');
    this.reloadBrowser();
  }

  private reloadBrowser(): void {
    window.location.reload();
  }

  private userHasRole(...roles: string[]): boolean {
    const user = this.userService.getCurrentUser();
    return roles.every(role => user.getRoles().includes(role));
  }

  userIsAbleToSeeEtkInAWP(): boolean {
    return this.appsService.appVisibleForUser('etk');
  }

  userIsAbleToSeePrintInAWP(): boolean {
    return this.userHasRole('awp_b2d_printcenter');
  }

  userIsAbleToSeeLeadsInAWP(): boolean {
    return this.userHasRole('awp_b2d_leads');
  }

  private setUserData(): void {
    const user = this.userService.getCurrentUser();
    this.businessPartnerId = user.getBusinessPartner()?.getBusinessPartnerId();
    this.businessNumber = user.getBusinessNumber(this.serviceCaseHolder.getActiveCase()?.getBrand());
    this.distributionPartnerNumber = user.getBusinessPartner()?.getDistributionPartnerNumber();
    this.outletNumber = user.getBusinessPartner()?.getOutletNumber();
    this.vehicleType = user.getSelectedVehicleType();
    this.brands = user.getBrandsOfSelectedBusinessPartner().map(brand => brand.valueOf()).join(',');
    this.locale = user?.getLocale();
    this.etkConfigurationBaseUrl = ConfigurationService.getUrlForPortalHost('etkConfig');
    this.printConfigurationBaseUrl = ConfigurationService.getUrlForPortalHost('printDataConfigurator');
  }

  private onAuthTokenChanged(): void {
    const userData = this.userService.getCurrentUser();
    if (userData) {
      this.authInfo = JSON.stringify(AuthInfoFactory.makeAuthInfo(
        userData,
        this.serviceCaseHolder.getActiveCase()?.getBrand()
      ));
    } else {
      this.authInfo = JSON.stringify({});
    }

    const oidcToken = this.authenticationTokenBearerService.oidcTokenSubject.getValue();
    if (oidcToken.access_token) {
      this.authHeaders = JSON.stringify(oidcToken.getHeaders());
    } else {
      this.authHeaders = '{}';
    }
  }
}
